import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import { PaymentOptionsNav } from '../components/PaymentOptionsNav.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import {
  requiresMultipleSignatories,
  MULTIPLE_SIGNATORIES_MESSAGE,
} from '../lib/signatories.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { STANDING_ORDERS_TEMPLATES } from '../email-templates/templates.js';
import { useContentMap } from '../content.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { formatOwners } from '../lib/owners.js';
import { formatCurrency } from './account-detail-shared.js';

/**
 * Present a standing-order monetary amount (a raw gateway string, e.g. "876.21")
 * as a currency with the account's own symbol (#436 / the cross-screen currency
 * rule #383/#415 — never hardcode €). Parses the string robustly (strips any
 * thousands separators, then parseFloat) and formats it with
 * `Intl.NumberFormat('en-IE', { style: 'currency', currency })` via the shared
 * `formatCurrency`. Returns the raw string unchanged when it can't be parsed so
 * a present-but-unparseable amount is never lost (unlike the bare formatter,
 * which would render '—').
 */
function formatAmount(raw?: string, currency = 'EUR'): string {
  if (raw == null) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return trimmed;
  const parsed = Number(trimmed.replace(/,/g, ''));
  if (!Number.isFinite(parsed)) return trimmed;
  return formatCurrency(parsed, currency || 'EUR');
}

/**
 * SCRN 35 — Standing Orders → Overview listing (CCF-72) and
 * SCRN 37 — Standing Orders → Create (CCF-14 regular + CCF-73 PTSB VISA).
 *
 * Faithful port of the Payments-homescreen Figma "Standing orders" flow, scoped
 * to the realized slice. The agent picks Standing orders → then either:
 *   - "Standing order overview" → an account → a listing of the account's
 *     standing orders (Payment Type "SO"), with a "Show all cancelled, lapsed
 *     and completed" toggle and per-row View/Amend (SCRN-35 / CCF-72), or
 *   - "Create standing order" → an account → "Create standing order" /
 *     "Create standing order to Visa", fills the beneficiary + schedule fields,
 *     confirms, and submits (SCRN-37 / CCF-14 + CCF-73).
 *
 * Backed by the Teller-ID-gated BFF via the generated typed client:
 *   - accounts: POST /api/customers/accounts
 *   - listing:  POST /api/payments/standing-order-list  (includeInactive toggle)
 *   - create:   POST /api/payments/standing-order-create
 *
 * The per-row Amend/Cancel detail panel (SCRN-36 / CCF-101) loads the standing
 * order's full detail (POST /api/payments/standing-order → the dedicated gateway
 * standing-order-details op), shows the read-only IBAN/BIC/type/status and
 * pre-fills the editable fields — beneficiary name/reference + remit data AND
 * the schedule (frequency, regular pay date, regular amount, number of
 * payments, last-day-of-month). Once the agent ticks "Confirm all details" it
 * offers "Amend standing order" (POST /api/payments/standing-order-amend → the
 * dedicated standing-order-amend op) and "Cancel standing order" (POST
 * /api/payments/standing-order-cancel). Wiring SO detail/amend onto the
 * bill-payment family ops returned gateway error 933 (issue #113); the dedicated
 * ops accept the full CCF-101 editable set. The shared agent banner lives in the
 * app shell, so this screen renders only the `<main>` content.
 */

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  /** ISO currency of the account (e.g. 'EUR', 'USD') — drives the amount symbol. */
  Currency?: string;
  /**
   * Account-holder name(s) (CCF-80). The BFF adapter (`deriveOwners`) joins
   * joint holders with an ampersand ("A & B"); a single-holder account carries
   * one name. Sourced from `customer/accounts/list` — the same field the
   * Dashboard tile and Historic Transactions read. Drives the "Account owners"
   * label so ALL owners are shown wherever the owner is displayed (#414/#463).
   */
  Owners?: string;
  /**
   * Raw 2-char Unibanks account-type code from the `customer/accounts/list`
   * association (e.g. `CS` Switch Current, `US` US$ Call), threaded by the BFF
   * as `GatewayAccountType` (issue #75). Drives the create-standing-order
   * eligibility filter (issue #529).
   */
  GatewayAccountType?: string;
  /**
   * Normalised per-account signatory count (issue #799), threaded by the BFF
   * from the gateway `customer/accounts/list` `NumofSignatories`. Drives the
   * >1-signatory Standing Order cancellation block via the shared
   * {@link requiresMultipleSignatories} helper. Absent → not blocked.
   */
  Signatories?: number | string;
}

/**
 * Unibanks account types ELIGIBLE to deduct a standing order (issue #529) —
 * the granular 2-char `AccountType` code from `customer/accounts/list`, per
 * the UAT-supplied list on the ticket. The gateway itself declines an
 * ineligible deduct account with ErrorNumber 9161 ("Product type for deduct
 * account not allowing SO"); this filter stops the agent being offered such
 * accounts on the Create flow in the first place. The gateway stays the
 * authoritative gate — the inline 9161 error remains as the backstop.
 */
const SO_ELIGIBLE_ACCOUNT_TYPE_CODES: ReadonlySet<string> = new Set([
  '11', // Demand Deposit
  '1A', // Current
  '2A', // Euro Current
  '4A', // PTSB Current Account 1
  '4B', // PTSB Current Account 2
  '4C', // PTSB Current Account 3
  '4D', // PTSB Current Account 4
  '56', // Prestige HICA
  'CA', // Current Account
  'CB', // Loyalty Account
  'CD', // Cash Extra
  'CF', // Cashflow
  'CG', // Standard Current A/C
  'CI', // Business Current A/C
  'CL', // Cashline Account
  'CM', // Merit Account
  'CN', // Loyalty Current A/C
  'CP', // Current Account Plus
  'CR', // Current Acct Recoveries
  'CS', // Switch Current A/C
  'CT', // Connect Account
  'CX', // Student Current A/C
  'CY', // My Cash Account
  'E1', // Everyday Bank A/C 1
  'E2', // Everyday Bank A/C 2
  'E3', // Standard Bank Acc
  'E4', // permanent tsb Current
  'E5', // Explore Account
  'E6', // Basic Payment A/C
  'HI', // Prestige HICA
  'LC', // Non-Personal Demand Deposit
  'VA', // Visa Application
  'XA', // Euro Current Account
  'XP', // Euro Current A/c Plus
]);

/**
 * True when the account may deduct a standing order (issue #529). An account
 * whose type code is absent/unclassifiable is STILL offered (fail-open) — the
 * gateway's 9161 inline error is the authoritative backstop, and hiding an
 * account we cannot classify would be a worse agent experience.
 */
function isStandingOrderEligible(a: AccountRow): boolean {
  const code = (a.GatewayAccountType ?? '').trim().toUpperCase();
  return code === '' || SO_ELIGIBLE_ACCOUNT_TYPE_CODES.has(code);
}

/** A standing-order listing row — Payment Type "SO" (CCF-72). */
interface StandingOrderRow {
  PaymentId?: string;
  Name?: string;
  Reference?: string;
  Status?: string;
  Retry?: string;
  Type?: string;
  Frequency?: string;
  Amount?: string;
  NumberOfPaymentsMade?: string;
  /** Unibanks keys — threaded to the detail/amend/cancel ops (CCF-101). */
  PaymentsSeq?: string;
  PaymentsNo?: string;
}

/**
 * Full standing-order detail (SCRN-36 / CCF-101). The beneficiary name/reference
 * + remit data AND the schedule (frequency, regular pay date, regular amount,
 * number of payments, last-day-of-month) are EDITABLE via the dedicated
 * standing-order-amend op (#113); the IBAN/BIC, status, type and
 * number-of-payments-made are READ-ONLY (see docs/contract-conflicts.md row 15).
 */
interface StandingOrderDetail {
  Found?: boolean;
  AccountNumber?: string;
  PaymentId?: string;
  Name?: string;
  Reference?: string;
  Status?: string;
  Retry?: string;
  Type?: string;
  Frequency?: string;
  Amount?: string;
  NumberOfPaymentsMade?: string;
  BeneficiaryIban?: string;
  BeneficiaryBic?: string;
  BeneficiaryName?: string;
  BeneficiaryReference?: string;
  RemitData?: string;
  PaymentsSeq?: string;
  PaymentsNo?: string;
  /**
   * Editable schedule fields returned by the dedicated standing-order-details op
   * (CCF-101 / #113) — used to PRE-FILL the amend sub-form. PaymentFrequency is
   * our internal token (the BFF maps the gateway enum back); RegPayDate is the
   * gateway DD/MM/YYYY; KeepEoM is 'Y'/'N'.
   */
  PaymentFrequency?: string;
  RegPayDate?: string;
  RegAmount?: string;
  NumOfPymts?: string;
  KeepEoM?: string;
  /** Carried on a not-found / failed detail response (CCF-101) for inline UX. */
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * Build a concise inline error string from the gateway's not-found / failed
 * detail response, surfacing the real Code + Message beside the action (#103).
 * Falls back to a generic, recoverable message when the gateway gave neither.
 */
function detailErrorText(d?: {
  ResultCode?: string;
  ResultMessage?: string;
}): string {
  const code = (d?.ResultCode ?? '').trim();
  const message = (d?.ResultMessage ?? '').trim();
  if (code && message) return `Could not load details — ${message} (${code}).`;
  if (message) return `Could not load details — ${message}.`;
  if (code) return `Could not load details — gateway code ${code}.`;
  return 'Could not load standing order details. Please retry.';
}

/**
 * Gateway result codes whose raw message is generic ("Unable to Process
 * Transaction") mapped to a plain-language explanation for the inline error.
 *
 * Completed from the AUTHORITATIVE catalogue in
 * requirements/unibanks-reference/API_Error_Codes.csv (the `Create Standing
 * Order` + `Amend Standing Order` API_Names) — the same #284/#286/#376/#377
 * rule as the funds-transfer map: resolve from the catalogue rather than
 * hand-adding one code per ticket, so an unmapped decline can never again show
 * the bare "NNNN — Unable to Process Transaction". This screen drives both the
 * create and amend/cancel ops, so a single shared map covers all of them.
 *
 * 899 = the regular pay date is in the past (also blocked pre-submit by the
 * amend form, #135). 9153 = Invalid Visa Number (#268). 9161 = product type for
 * the deduct account does not allow standing orders — the reported #504 case
 * that surfaced only the generic gateway message.
 */
const FRIENDLY_GATEWAY_CODE: Record<string, string> = {
  '42': 'Invalid date',
  '100': 'Account not found',
  '124': 'More than one signature is required — check the mandate',
  '153': 'The channel is closed',
  '154': 'Invalid channel received',
  '289': 'No account-customer correlation',
  '345': 'Charge record does not exist',
  '504': 'Batch type (Batyp) not found',
  '598': 'The IBAN is inactive',
  '599': 'Invalid IBAN entered',
  '604': 'No BSUSP record',
  '680': 'Invalid character entered',
  '702': 'A mandate cannot be set up with the IBAN supplied',
  '828': 'Invalid Teller entered',
  '887': 'Standing order does not exist',
  '894': 'Regular pay date must be after the first pay date',
  '895': 'Due date should be the last day of the month',
  '899':
    'Regular pay date cannot be in the past — choose today or a future date',
  '900': 'Due date must precede the final pay date',
  '905': 'Regular payment date is too far in the past',
  '907': 'Invalid beneficiary account type',
  '926': 'Regular payment exceeds the monthly maximum allowed',
  '936': 'The account is not active',
  '9101': 'The customer number does not exist',
  '9102': 'Invalid card-customer relation',
  '9103': 'Invalid online card number',
  '9104': 'Invalid card type for the given online card',
  '9153': 'Invalid Visa Number',
  '9154': 'Issue with the BIC number',
  '9157': 'Use the Internal Transfer to PTSB Visa option instead',
  '9161': 'Product type for the deduct account does not allow standing orders',
  '9162': 'Beneficiary name not supplied',
  '9163': 'No mortgage suspense account',
  '9164': 'Credit account type not allowed for internal transfers',
  '9165': 'Credit account stopped for internal transfers',
  '9170': 'Cannot set End-of-Month for daily frequencies',
  '9171': 'Invalid frequency',
  '9172': 'Cannot change a one-off payment',
  '9173': 'Invalid standing-order type',
  '9174': 'Invalid standing-order status',
  '9175': 'Cannot amend the beneficiary name',
  '9176': 'Cannot amend the beneficiary reference',
  '9177': 'No remittance for a mortgage payment',
  '9178': 'Remittance should be blank for a Visa SI account',
  '9179': 'Invalid value for Keep End Of Month',
  '9180': 'Cannot alter the number of payments when an end date is set',
  '9181': 'The calculated number of payments is too large',
};

/**
 * Maximum standing-order rows shown per page in the overview list (#437 —
 * @dylan219: "make each page a maximum of 10 and allow user click next to see the
 * next 10"). Pagination is client-side over the already-loaded list.
 */
const STANDING_ORDERS_PAGE_SIZE = 10;

/**
 * Build a concise inline error string from a failed amend/cancel write response,
 * surfacing the gateway ResultCode + ResultMessage beside the action so the
 * agent sees WHY it failed and can recover (@dylan219 #80/#82/#94). A 403 is an
 * RBAC/Teller-ID problem, not a gateway-record outcome, so it keeps its own
 * message.
 *
 * Issue #248 — the RBAC *action* denial (`requirePermission` refuses a blocked
 * action with 403 `{ error: 'forbidden' }`, e.g. a restricted role attempting a
 * standing-order write on an OFFLINE journey per increment 1) surfaces
 * @dylan219's authoritative verbatim copy inline beside the button — never a raw
 * 403 or a generic error. `errorBody` is the openapi-fetch parsed error body;
 * only when it is that specific `{ error: 'forbidden' }` shape do we show the
 * action-denied copy. Any OTHER 403 (e.g. the Teller-ID gate's
 * `{ error: 'invalid_teller_id' }`) keeps its own distinct message.
 */
function actionErrorText(
  status: number,
  d: { ResultCode?: string; ResultMessage?: string } | undefined,
  verb: 'amend' | 'cancel' | 'create',
  errorBody?: unknown,
): string {
  const blocked = resolveActionBlockMessage(status, errorBody);
  if (blocked !== null) return blocked;
  if (status === 403) return 'Forbidden: check your Teller ID and permissions.';
  const code = (d?.ResultCode ?? '').trim();
  const message = (d?.ResultMessage ?? '').trim();
  const fallback = `Could not ${verb} the standing order. Please retry.`;
  // A few gateway codes carry a generic "Unable to Process Transaction"
  // message; surface a readable explanation so the agent can recover (#135).
  if (code && FRIENDLY_GATEWAY_CODE[code])
    return `${FRIENDLY_GATEWAY_CODE[code]} (${code}).`;
  if (code && message) return `${message} (${code}).`;
  if (message) return message;
  if (code) return `Gateway code ${code}. Please retry.`;
  return fallback;
}

/** Allowed characters for the editable remit field (mirrors CCF-67/101). */
const REMIT_CHARS = /^[A-Za-z0-9/\-?:().,'+ ]*$/;

/**
 * Validate an editable beneficiary/remit field (CCF-101). Returns an error
 * message or '' when valid. Mirrors the bill-payment screen's validator.
 */
function validateDetailField(
  label: string,
  value: string,
  maxLen: number,
  required: boolean,
  isRemit = false,
): string {
  if (!value) return required ? `${label} is required.` : '';
  if (value.length > maxLen)
    return `${label} must be ${maxLen} characters or fewer.`;
  if (!(isRemit ? REMIT_CHARS : BENEFICIARY_CHARS).test(value))
    return `${label} contains invalid characters.`;
  return '';
}

/**
 * Standing-order lifecycle status → display label (CCF-72 / #606).
 *
 * The gateway returns the raw `Status` token from the enum in
 * `requirements/unibanks-reference/Unibanks_GetAccountPayments.yaml`
 * (`["Active","New","Cancel","Trans","Switch","Lapsed","Compld","Error"]`) and
 * `Unibanks_GetStandingOrder.yaml`. The readable labels below come **only** from
 * that reference — no invented display text:
 *
 * - `TRANS` → **Transfer**: the reference's note-1 lists this status as
 *   "Tranfr (Transfer)" and note-2 states the value maps to a Unibanks status of
 *   'T' (Transfer). (This corrects the earlier ad-hoc "In transit" gloss from
 *   #480, which changed only the view-only *behaviour*, not the sourced label.)
 * - `COMPLD` → **Completed**: note-1 gives "Compld (Completed)".
 * - `CANCEL` → **Cancelled**: normalised via the `CANCEL`-prefix rule below (#480).
 * - `SWITCH` stays **Switch**: the reference describes it as "Account Switch Out"
 *   but returns/displays the token *Switch* itself — no expansion is defined, so
 *   we render it verbatim rather than invent wording.
 * - `ACTIVE`/`NEW`/`LAPSED`/`ERROR` are already human-readable enum values.
 *
 * Any status not in this map falls back to the raw value so a new gateway status
 * surfaces rather than being silently dropped.
 */
const SO_STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'Active',
  NEW: 'New',
  CANCELLED: 'Cancelled',
  TRANS: 'Transfer',
  SWITCH: 'Switch',
  LAPSED: 'Lapsed',
  COMPLETED: 'Completed',
  COMPLD: 'Completed',
  ERROR: 'Error',
};

/**
 * Human display label for a gateway standing-order status. Normalises + maps the
 * known families onto their tidy label — in particular the gateway's cancelled
 * value is `CANCEL` (#480), which would otherwise render as the raw token. Any
 * unmapped status falls back to the raw value so a new gateway status surfaces.
 */
function soStatusLabel(status?: string): string {
  if (!status) return '';
  const s = status.trim().toUpperCase();
  if (s.startsWith('CANCEL')) return 'Cancelled';
  return SO_STATUS_LABEL[s] ?? status;
}

/**
 * Standing-order payment TYPE code → display label (#505 / #614 / CCF-72). The
 * gateway `payment/pay-list` op returns the type as a code (`CT`, `IT`, and also
 * `TL`, `ML`, `TO` per the contract examples in
 * `requirements/unibanks-reference/Unibanks_GetAccountPayments.yaml`). Authoritative
 * mappings: `CT` → "Credit Transfer", `IT` → "Internal Transfer" (#505) and
 * `TL` → "Term Loan" (product-confirmed in #614). The remaining `ML`/`TO` (and
 * any unknown) labels are still pending a product confirmation (@dylan219) — so we
 * deliberately do NOT guess them and fall back to the raw code unchanged.
 */
const SO_TYPE_LABEL: Record<string, string> = {
  CT: 'Credit Transfer',
  IT: 'Internal Transfer',
  TL: 'Term Loan',
};

/**
 * Human display label for a gateway standing-order type code (#505 / #614 /
 * CCF-72). Maps the known codes onto their label; any other code (`ML`/`TO`/
 * unknown) falls back to the raw code trimmed (empty stays empty) — those labels
 * are pending @dylan219 confirmation, so we must not guess them.
 */
function soTypeLabel(code?: string): string {
  if (!code) return '';
  const c = code.trim();
  return SO_TYPE_LABEL[c.toUpperCase()] ?? c;
}

/**
 * Retry-column display for a gateway standing order (#505 / CCF-72). The gateway
 * `payment/pay-list` op returns `RetryInd` as a `Y`/`N` flag; the overview table
 * shows "R" when a retry is available and blank otherwise. `R` is also accepted
 * (defensive, since the column value has historically been rendered as "R").
 */
function retryDisplay(v?: string): string {
  const s = (v ?? '').trim().toUpperCase();
  return s === 'Y' || s === 'R' ? 'R' : '';
}

/**
 * Standing-order statuses that are EDITABLE (amendable / cancellable) via the
 * detail panel (#480 — @dylan219, 2026-07-23): "Some standing orders are view
 * only and some are editable, this is completely driven by the Status field."
 * The authoritative status→action matrix (#655 / CCRS-3354, supervisor):
 *
 *   - **New**, **Active**, **Retry** → editable
 *   - Cancelled (`CANCEL`), Lapsed, Completed (`COMPLD`) and Transfer (`TRANS`)
 *     → view-only, as is a blank or otherwise-unknown status.
 *
 * `NEW` was NOT enumerated in #480, so it previously fell **closed** to
 * view-only; #655 fills that gap — a New standing order must be amendable. This
 * is a consistent extension of #480 (Cancelled/Lapsed/Completed stay view-only,
 * Active/Retry stay editable), not a reversal of it.
 *
 * This is an **explicit allow-list**: the detail panel exposes a payment
 * state-change control, so it fails **closed** to view-only for any status we
 * don't positively recognise as editable — an unknown upstream state must not
 * become editable just because the gateway might reject the amend/cancel later
 * (deny-by-default, R4). A genuinely-editable status therefore has to be named
 * here. In-retry orders keep their `ACTIVE` status (the retry state is carried
 * by the separate `Retry` = "R" field, not the Status token), but `RETRY` is
 * allow-listed too so a future gateway that surfaces it as a Status value stays
 * editable per @dylan219's spec.
 */
const EDITABLE_SO_STATUSES: ReadonlySet<string> = new Set([
  'ACTIVE',
  'NEW',
  'RETRY',
]);

/**
 * Whether a standing order may be edited (amended/cancelled) via the detail
 * panel (#480 / #655). Editable **only** for the allow-listed New / Active /
 * Retry statuses; every other status — the Cancelled family (`CANCEL` /
 * `CANCELLED` /
 * `CANCELED`), Lapsed, Completed (`COMPLD`), in-transit (`TRANS`), and any
 * **blank or unknown** status — fails **closed to view-only**. This is a
 * payment state-change control on a deny-by-default project, so an unrecognised
 * upstream state is never treated as editable.
 */
function isStandingOrderEditable(status?: string): boolean {
  const s = (status ?? '').trim().toUpperCase();
  return EDITABLE_SO_STATUSES.has(s);
}

/**
 * Whether the per-row trashcan (cancel) is enabled for a standing order.
 *
 * Cancellability tracks the **same** editable status allow-list as the detail
 * panel (New / Active / Retry — see {@link EDITABLE_SO_STATUSES}). Per the
 * authoritative CCF-72 / CCRS-3354 status→action matrix a status is either
 * **Editable (amend AND cancel)** or **View Only** — the two actions are never
 * split — so a NEW order that is amendable must also be cancellable via the
 * trashcan (#655, @philipc39 follow-up 2026-08-19). Previously this predicate
 * only allowed `ACTIVE`, so a NEW order rendered the trashcan disabled with a
 * "this standing order is already new and cannot be cancelled" tooltip even
 * though the amend panel was editable — an inconsistency this fix removes.
 *
 * The Cancelled / Lapsed / Completed (and in-transit `TRANS`) family stays
 * view-only, so the trashcan is disabled for them (only ever visible via the
 * "Show all cancelled, lapsed and completed" toggle) — cancelling an
 * already-cancelled order made the real gateway reject with the generic
 * "Unable to Process Transaction (888)" (#277), so disabling the control removes
 * that dead-click. Missing/unknown status is treated as cancellable (fail-open
 * to the gateway's own validation, which then surfaces its Code + Message
 * inline) — matching the historical trashcan behaviour for a blank status.
 */
function isStandingOrderCancellable(status?: string): boolean {
  const s = (status ?? '').trim().toUpperCase();
  return s === '' || isStandingOrderEditable(s);
}

/**
 * True when the standing order is a **Term Loan** (`TL`) order (issue #625 —
 * @dylan219). A Term-Loan standing order is treated specially:
 *
 *   1. The dedicated `standing-order-details` op FAILS for a TL order, so the
 *      detail panel MUST NOT fire it — the view is populated purely from the
 *      data already carried on the listing (LIST) row.
 *   2. The order is rendered **view-only** — no field is amendable / updatable
 *      and no Amend/Cancel action is offered (regardless of Status).
 *   3. Any field with no value on the listing row is left BLANK (the app's
 *      existing "—"/empty convention) — nothing is invented or fetched.
 *
 * The gateway `payment/pay-list` op returns the type as a code (`TL`, per the
 * contract examples in
 * `requirements/unibanks-reference/Unibanks_GetAccountPayments.yaml`; product-
 * confirmed `TL` → "Term Loan" in #614). The check is code-based and tolerant of
 * the already-mapped display label ("Term Loan"), so it holds whether the row
 * carries the raw code or the label.
 */
function isTermLoanStandingOrder(type?: string): boolean {
  const t = (type ?? '').trim().toUpperCase();
  return t === 'TL' || t === 'TERM LOAN';
}

/** Frequency options (Figma) → contract PaymentFrequency code. */
const FREQUENCIES = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'FORTNIGHTLY', label: 'Fortnightly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'BI_MONTHLY', label: 'Bi-Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'HALF_YEARLY', label: 'Half-Yearly' },
  { value: 'ANNUALLY', label: 'Yearly' },
] as const;

// Legacy/gateway frequency labels that map onto a current option token (#448:
// the ANNUALLY option's display label changed from "Annually" to "Yearly", but
// a standing-order listing row may still carry the older "Annually" label, so
// the amend pre-select must recognise both). The option VALUE ("ANNUALLY") is
// unchanged — only the human-visible label moved.
const FREQUENCY_LABEL_ALIASES: Readonly<Record<string, string>> = {
  annually: 'ANNUALLY',
};

// "Until further notice" = an open-ended standing order. The R4 gateway has no
// separate indefinite flag — NumOfPymts must be 1–999, and 999 is the documented
// open-ended value (payments.r4.yaml: an ACTIVE SO with 194 payments made carries
// NumOfPymts:999). See issue #144.
const UNTIL_FURTHER_NOTICE_NUM_PYMTS = '999';

/**
 * Map a frequency display LABEL (as carried on the listing row, e.g. "Monthly")
 * back to our internal token (e.g. "MONTHLY"), so the amend Frequency dropdown
 * can pre-select even if the detail op only returned the label (#113). Unknown
 * labels yield '' (no selection).
 */
function labelToFrequencyToken(label?: string): string {
  if (!label) return '';
  const direct = FREQUENCIES.find((f) => f.value === label);
  if (direct) return direct.value;
  const norm = label.trim().toLowerCase();
  const byLabel = FREQUENCIES.find((f) => f.label.toLowerCase() === norm);
  if (byLabel) return byLabel.value;
  return FREQUENCY_LABEL_ALIASES[norm] ?? '';
}

// CCF-73 limits the PTSB VISA standing order to the five base frequencies;
// Bi-Monthly and Half-Yearly are CCF-14 (regular-order) only. The two forms
// share this list, so the VISA variant filters to these.
const VISA_FREQUENCY_VALUES: ReadonlySet<string> = new Set([
  'WEEKLY',
  'FORTNIGHTLY',
  'MONTHLY',
  'QUARTERLY',
  'ANNUALLY',
]);

/** Allowed characters for the editable beneficiary fields (mirrors CCF-67). */
const BENEFICIARY_CHARS = /^[A-Za-z0-9/\-?:().,'+ ]*$/;

/**
 * Regular amount = the gateway's RegAmount: a monetary value, up to 7 integer
 * digits and exactly 2 decimal places (e.g. 876.21). Validated on submit; the
 * value sent to the API is always normalised to 2 decimals.
 */
const REG_AMOUNT_PATTERN = /^\d{1,7}(\.\d{2})?$/;

/** Strip everything except digits and a single decimal point (max 2 dp) as typed. */
function sanitizeAmountInput(raw: string): string {
  let cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) {
    // Keep only the first decimal point.
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, '');
    const [intPart = '', decPart = ''] = cleaned.split('.');
    cleaned = `${intPart.slice(0, 7)}.${decPart.slice(0, 2)}`;
  } else {
    cleaned = cleaned.slice(0, 7);
  }
  return cleaned;
}

/** Normalise a monetary amount to exactly 2 decimal places (100 → 100.00, 5.5 → 5.50). */
function normalizeAmount(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === '.') return trimmed;
  const [intPart = '', decPart = ''] = trimmed.split('.');
  return `${intPart === '' ? '0' : intPart}.${decPart.padEnd(2, '0').slice(0, 2)}`;
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  /** Render the dropdown read-only/uneditable (e.g. a view-only SO, #480). */
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => {
            if (!disabled) setOpen((o) => !o);
          }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={`w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between ${
            disabled
              ? 'bg-[#f3f4f6] text-[#6b7280] cursor-not-allowed'
              : 'bg-white'
          }`}
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Today as an ISO `YYYY-MM-DD` string (local time), for native date inputs. */
function isoToday(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Tomorrow as ISO `YYYY-MM-DD` — the earliest selectable pay date (future only). */
function isoTomorrow(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * The next "last day of month" as ISO `YYYY-MM-DD` — this month's last day if
 * it's still in the future, otherwise next month's. The R4 gateway has no way
 * to omit `RegPayDate` when `KeepEoM='Y'` (it's unconditionally required, both
 * on Create and Amend) — so ticking "Last day of month" auto-fills the pay
 * date instead of waiving it, which is what the agent actually needs (#288).
 */
function isoLastDayOfMonth(): string {
  const now = new Date();
  const toIso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
  const endOfThisMonth = toIso(
    new Date(now.getFullYear(), now.getMonth() + 1, 0),
  );
  if (endOfThisMonth > isoToday()) return endOfThisMonth;
  return toIso(new Date(now.getFullYear(), now.getMonth() + 2, 0));
}

/** Convert a native date input value (`YYYY-MM-DD`) to the gateway's `DD/MM/YYYY`. */
function isoToDdMmYyyy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return iso.trim();
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** Convert the gateway's `DD/MM/YYYY` to a native date input value (`YYYY-MM-DD`). */
function ddMmYyyyToIso(value: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Labelled text input with an inline error slot. */
function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  error,
  hint,
  readOnlyText,
  inputMode,
  onBlur,
  type = 'text',
  min,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  error?: string;
  hint?: string;
  readOnlyText?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  onBlur?: () => void;
  type?: 'text' | 'date';
  min?: string;
  disabled?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      {readOnlyText ? (
        <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] py-1.5 m-0">
          {value || placeholder || '—'}
        </p>
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode={inputMode}
          min={min}
          disabled={disabled}
          aria-invalid={!!error}
          className={`px-4 py-2 border rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 ${
            disabled ? 'bg-[#f3f4f6] text-[#6b7280]' : 'bg-white'
          } ${error ? 'border-[#dc2626]' : 'border-[#d1d5db]'}`}
        />
      )}
      <div className="min-h-[18px]">
        {error ? (
          <p
            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            role="alert"
          >
            {error}
          </p>
        ) : hint ? (
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Accessible toggle switch, faithful to the export's "Show all" toggle. */
function Toggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
        aria-checked={checked}
      />
      <label
        htmlFor={id}
        className="relative flex items-center cursor-pointer group"
      >
        <div
          className={`relative w-[44px] h-[24px] rounded-full transition-all ${
            checked ? 'bg-[#FF703E]' : 'bg-[#d1d5db]'
          } peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]`}
        >
          <div
            className={`absolute top-[2px] left-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform ${
              checked ? 'translate-x-[20px]' : 'translate-x-0'
            }`}
          ></div>
        </div>
        <span className="ml-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280] select-none">
          {label}
        </span>
      </label>
    </div>
  );
}

export function StandingOrdersScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  // #637 inc 21 — the resolved content-catalogue map, threaded into the SEPA CT
  // Recall email template so its editable wording (subject/title/advisory
  // bullets/labels/reason options/instruction) reflects any admin reword;
  // absent-key defaults keep the output unchanged.
  const contentMap = useContentMap();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [option, setOption] = useState(''); // '' | 'create' | 'overview'
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isVisa, setIsVisa] = useState(false);

  // Overview (SCRN-35 / CCF-72) state.
  const [overviewAccount, setOverviewAccount] = useState('');
  const [orders, setOrders] = useState<StandingOrderRow[]>([]);
  const [showOrders, setShowOrders] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  // Client-side pagination (#437): the standing-orders list is shown a maximum of
  // STANDING_ORDERS_PAGE_SIZE (10) rows per page, and the agent pages through with
  // the Previous/Next controls. `page` is the zero-based current page; it is reset
  // to 0 whenever the list is (re)loaded or the account changes.
  const [page, setPage] = useState(0);
  // #829 (server-side pagination): the gateway returns at most ~100 standing
  // orders per call. `hasMore` mirrors the response `hasMore` for the CURRENTLY
  // loaded rows; the cursor is the continuation to fetch the NEXT ~100. When the
  // agent steps to a UI page whose rows aren't loaded yet AND `hasMore`, we
  // lazily re-call standing-order-list with the cursor and APPEND the next block
  // (page 11 → 2nd call, page 21 → 3rd, …). So Previous/Next can walk past page
  // 10 when there are >100 orders, without a jump-to-page control (@dylan219
  // 2026-09-24). `othersStream` (two-stream, CCF-72): the active stream is paged
  // first, then the Others (cancelled/lapsed/completed) stream on its own cursor.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef<{ seq?: string; no?: string; others?: boolean }>({});
  const [viewAmendId, setViewAmendId] = useState<string | null>(null);
  // Per-row trashcan cancel (#267) — the standing order the agent is being
  // asked to confirm cancelling, plus the in-flight flag and inline error. The
  // cancel only fires AFTER the agent confirms in the dialog.
  const [deleteTarget, setDeleteTarget] = useState<StandingOrderRow | null>(
    null,
  );
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Monotonic sequence so a slow earlier listing can't overwrite a newer one
  // (the agent switches account / toggles the filter before a request resolves).
  const listSeq = useRef(0);

  // View/Amend/Cancel detail panel (SCRN-36 / CCF-101) state.
  const [detail, setDetail] = useState<StandingOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Non-transient inline error for a failed detail load — surfaces the gateway
  // Code + Message beside the action (recoverable), rather than only a fleeting
  // toast + a blank panel (issue #103).
  const [detailError, setDetailError] = useState('');
  const [confirmDetails, setConfirmDetails] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRef, setEditRef] = useState('');
  const [editRemit, setEditRemit] = useState('');
  const [nameError, setNameError] = useState('');
  const [refError, setRefError] = useState('');
  const [remitError, setRemitError] = useState('');
  // Editable SCHEDULE fields (CCF-101 / #113) — pre-filled from the detail op.
  const [editFrequency, setEditFrequency] = useState('');
  const [editPayDate, setEditPayDate] = useState(''); // native ISO YYYY-MM-DD
  const [editAmount, setEditAmount] = useState('');
  const [editLastDay, setEditLastDay] = useState(false);
  const [editEndOption, setEditEndOption] = useState(''); // 'until' | 'count'
  const [editNumPayments, setEditNumPayments] = useState('');
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>(
    {},
  );
  // Inline, recoverable amend/cancel failure — surfaces the gateway Code +
  // Message beside the action (not only a fleeting toast), per @dylan219 #80/82/94.
  const [amendError, setAmendError] = useState('');
  const detailSeq = useRef(0);

  // Form fields.
  const [iban, setIban] = useState('');
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [remit, setRemit] = useState('');
  const [frequency, setFrequency] = useState('');
  const [payDate, setPayDate] = useState('');
  const [amount, setAmount] = useState('');
  const [lastDayOfMonth, setLastDayOfMonth] = useState(false);
  const [endOption, setEndOption] = useState(''); // 'until' | 'count'
  const [numPayments, setNumPayments] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  // Inline, recoverable create-failure — surfaces the gateway Code + Message
  // beside the Complete button so the agent sees WHY it failed and can retry,
  // rather than only a fleeting toast (@dylan219 #80/#82/#94; #141).
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  function resetForm(): void {
    setShowForm(false);
    setIsVisa(false);
    setIban('');
    setName('');
    setReference('');
    setRemit('');
    setFrequency('');
    setPayDate('');
    setAmount('');
    setLastDayOfMonth(false);
    setEndOption('');
    setNumPayments('');
    setConfirmed(false);
    setCompleted(false);
    setErrors({});
  }

  function openForm(visa: boolean): void {
    resetForm();
    setIsVisa(visa);
    setShowForm(true);
  }

  /** Clear the open detail panel + its edit buffer (SCRN-36 / CCF-101). */
  function resetDetail(): void {
    setViewAmendId(null);
    setDetail(null);
    setDetailError('');
    setConfirmDetails(false);
    setEditName('');
    setEditRef('');
    setEditRemit('');
    setNameError('');
    setRefError('');
    setRemitError('');
    setEditFrequency('');
    setEditPayDate('');
    setEditAmount('');
    setEditLastDay(false);
    setEditEndOption('');
    setEditNumPayments('');
    setScheduleErrors({});
    setAmendError('');
  }

  function resetOverview(): void {
    setOverviewAccount('');
    setOrders([]);
    setPage(0);
    setShowOrders(false);
    setIncludeInactive(false);
    setDeleteTarget(null);
    setDeleteError('');
    resetDetail();
  }

  /**
   * Load the account's standing orders (CCF-72). `inactive` selects the
   * "Show all cancelled, lapsed and completed" view: the BFF returns the active
   * orders COMBINED with the non-active ones (active on top, the cancelled /
   * lapsed / completed appended below). The default shows only active orders.
   */
  async function loadOrders(
    accountNumber: string,
    inactive: boolean,
  ): Promise<void> {
    if (!accountNumber) return;
    const seq = ++listSeq.current;
    setOrdersLoading(true);
    setError('');
    setDeleteTarget(null);
    setDeleteError('');
    resetDetail();
    // First page only — the next ~100-row block is fetched lazily by `loadMore`
    // when the agent steps past the loaded rows (#829).
    cursorRef.current = {};
    setHasMore(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order-list', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber,
          ...(inactive ? { includeInactive: true } : {}),
        },
      });
      if (seq !== listSeq.current) return;
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Could not load standing orders (${response.status}).`,
        );
        return;
      }
      const rows = (data.Payments ?? []) as unknown as StandingOrderRow[];
      setOrders(rows);
      setHasMore(data.hasMore ?? false);
      cursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
        others: data.othersStream ?? false,
      };
      setPage(0);
      setShowOrders(true);
      if (!rows.length)
        toast.info(
          inactive
            ? 'No cancelled, lapsed or completed standing orders.'
            : 'No standing orders available.',
        );
    } catch {
      if (seq === listSeq.current) setError('Request failed.');
    } finally {
      if (seq === listSeq.current) setOrdersLoading(false);
    }
  }

  /**
   * #829: fetch the NEXT ~100-order block from the gateway using the
   * continuation cursor and APPEND it to the loaded rows. Called when the agent
   * steps to a UI page whose rows aren't loaded yet and `hasMore` is true. The
   * `othersStream` flag (CCF-72 two-stream) is threaded back so the BFF pages the
   * Others stream once the active stream is exhausted. Guards against a
   * concurrent fetch (`loadingMore`) and a missing cursor. On failure the loaded
   * rows are left intact and `hasMore` is cleared so "Next" settles on the last
   * loaded page rather than retrying forever; the agent can retry by re-selecting
   * the account.
   */
  async function loadMore(): Promise<boolean> {
    if (loadingMore || !hasMore) return false;
    const cursor = cursorRef.current;
    if (!cursor.seq && !cursor.no) return false;
    setLoadingMore(true);
    try {
      const { data, error: apiError } = await api.POST(
        '/payments/standing-order-list',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber: overviewAccount,
            ...(includeInactive ? { includeInactive: true } : {}),
            ...(cursor.seq ? { nextPaymentsSeq: cursor.seq } : {}),
            ...(cursor.no ? { nextPaymentsNo: cursor.no } : {}),
            ...(cursor.others ? { othersStream: true } : {}),
          },
        },
      );
      if (apiError || !data) {
        setHasMore(false);
        return false;
      }
      const more = (data.Payments ?? []) as unknown as StandingOrderRow[];
      setOrders((cur) => [...cur, ...more]);
      setHasMore(data.hasMore ?? false);
      cursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
        others: data.othersStream ?? false,
      };
      return more.length > 0;
    } catch {
      setHasMore(false);
      return false;
    } finally {
      setLoadingMore(false);
    }
  }

  /**
   * Step to the next UI page. If the next page's rows aren't loaded yet and the
   * gateway signals more, fetch the next ~100-row block first, then advance
   * (#829). "Next" is disabled only on the last loaded page when `hasMore` is
   * false (handled in the render).
   */
  async function goToNextPage(): Promise<void> {
    const nextStart = (page + 1) * STANDING_ORDERS_PAGE_SIZE;
    if (nextStart >= orders.length && hasMore) {
      await loadMore();
    }
    setPage((p) => p + 1);
  }

  function onOverviewAccountChange(value: string): void {
    setOverviewAccount(value);
    setIncludeInactive(false);
    setOrders([]);
    setPage(0);
    setShowOrders(false);
    resetDetail();
    void loadOrders(value, false);
  }

  function onToggleInactive(): void {
    const next = !includeInactive;
    setIncludeInactive(next);
    if (overviewAccount) void loadOrders(overviewAccount, next);
  }

  /**
   * Open the View/Amend/Cancel detail panel for a standing order (SCRN-36 /
   * CCF-101). Loads the full detail (with the Unibanks keys from the listing
   * row) and seeds the editable beneficiary fields.
   */
  async function openDetail(row: StandingOrderRow): Promise<void> {
    if (!row.PaymentId) return;
    const seq = ++detailSeq.current;
    setViewAmendId(row.PaymentId);
    setDetail(null);
    setDetailError('');
    setConfirmDetails(false);

    // Term Loan (TL) standing orders (issue #625 — @dylan219): the dedicated
    // `standing-order-details` op FAILS for a TL order, so do NOT fire it.
    // Populate the view purely from the data already on the listing row and
    // render it VIEW-ONLY (see `detailEditable` below, which is forced off for a
    // TL order). Any field the listing row doesn't carry (e.g. Beneficiary
    // IBAN/BIC/name/reference, remit, schedule) is simply left BLANK — nothing
    // is invented or fetched.
    if (isTermLoanStandingOrder(row.Type)) {
      setDetail({
        Found: true,
        Type: row.Type,
        Frequency: row.Frequency,
        Amount: row.Amount,
        NumberOfPaymentsMade: row.NumberOfPaymentsMade,
        Retry: row.Retry,
        Status: row.Status,
        Name: row.Name,
        Reference: row.Reference,
        PaymentsSeq: row.PaymentsSeq,
        PaymentsNo: row.PaymentsNo,
      });
      setDetailError('');
      setAmendError('');
      // Populate the view-only panel from the data ALREADY on the listing row
      // (issue #665 — @dylan219 / #625): the panel's Beneficiary name/reference,
      // Frequency and Regular amount fields render from these edit buffers, so —
      // even though they stay disabled for a TL order — they must be SEEDED from
      // the list row (Name/Reference/Frequency/Amount) rather than left blank.
      // No detail op is fired (it fails for TL); a field the list row doesn't
      // carry (Beneficiary IBAN/BIC, remit data, regular pay date, number of
      // payments) is left BLANK per the "—"/empty convention — nothing invented.
      setEditName(row.Name ?? '');
      setEditRef(row.Reference ?? '');
      setEditRemit('');
      setNameError('');
      setRefError('');
      setRemitError('');
      // Frequency comes off the list row as a display LABEL ("Monthly"); map it
      // back to the internal token so the Frequency dropdown shows the value.
      setEditFrequency(labelToFrequencyToken(row.Frequency));
      setEditPayDate('');
      setEditAmount(normalizeAmount(row.Amount ?? ''));
      setEditLastDay(false);
      setEditEndOption('');
      setEditNumPayments('');
      setScheduleErrors({});
      setDetailLoading(false);
      return;
    }

    setDetailLoading(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: overviewAccount,
          // PaymentsSeq is the dedicated detail op's key (#113); the opaque
          // PaymentId is the mock-resolution fallback.
          paymentsSeq: row.PaymentsSeq ?? '',
          ...(row.PaymentId ? { paymentId: row.PaymentId } : {}),
        },
      });
      // A newer open/load superseded this one — drop the stale response.
      if (seq !== detailSeq.current) return;
      if (apiError || !data || (data as { Found?: boolean }).Found === false) {
        // Surface the gateway Code + Message INLINE beside the action
        // (non-transient, recoverable) — not only a fleeting toast that leaves
        // a blank panel (#103). A 403 is an RBAC/Teller-ID problem, not a
        // gateway-record outcome, so it keeps its own message.
        const inline =
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : detailErrorText(
                data as { ResultCode?: string; ResultMessage?: string },
              );
        setDetailError(inline);
        toast.error(inline);
        return;
      }
      // The dedicated `standing-order-details` op (#113) returns the FULL record
      // — including the schedule fields (PaymentFrequency / RegAmount /
      // RegPayDate / NumOfPymts / KeepEoM) — so the detail response is the
      // authoritative pre-fill source. The listing row is merged BENEATH it
      // (detail wins) as a defensive fallback for any field the op omits (#86).
      const fetched = data as unknown as StandingOrderDetail;
      const d: StandingOrderDetail = {
        Type: row.Type,
        Frequency: row.Frequency,
        Amount: row.Amount,
        NumberOfPaymentsMade: row.NumberOfPaymentsMade,
        Retry: row.Retry,
        Status: row.Status,
        Name: row.Name,
        Reference: row.Reference,
        PaymentsSeq: row.PaymentsSeq,
        PaymentsNo: row.PaymentsNo,
        ...Object.fromEntries(
          Object.entries(fetched).filter(
            ([, v]) => v !== undefined && v !== null && v !== '',
          ),
        ),
      };
      setDetail(d);
      setDetailError('');
      setAmendError('');
      setEditName(d.BeneficiaryName ?? '');
      setEditRef(d.BeneficiaryReference ?? '');
      setEditRemit(d.RemitData ?? '');
      setNameError('');
      setRefError('');
      setRemitError('');
      setScheduleErrors({});
      // Pre-fill the editable SCHEDULE fields (CCF-101 / @dylan219 #82/#86/#87).
      // PaymentFrequency is the internal token; fall back to mapping the listing
      // row's frequency LABEL when the op did not return a token.
      const freq = d.PaymentFrequency ?? labelToFrequencyToken(d.Frequency);
      setEditFrequency(freq);
      setEditPayDate(ddMmYyyyToIso(d.RegPayDate ?? ''));
      setEditAmount(normalizeAmount(d.RegAmount ?? d.Amount ?? ''));
      setEditLastDay(
        freq === 'MONTHLY' && (d.KeepEoM ?? '').toUpperCase() === 'Y',
      );
      // Number of payments → until-further-notice vs a count. Empty/"0" and the
      // open-ended sentinel 999 (see #144: the R4 gateway has no separate
      // indefinite flag, so "until further notice" is encoded as NumOfPymts=999)
      // both map back to the "until further notice" radio; anything else is a
      // specified count. Without the 999 check a SO amended to "until further
      // notice" reads back as "Specified number: 999" (#198).
      const numPaid = (d.NumOfPymts ?? '').trim();
      if (
        numPaid &&
        numPaid !== '0' &&
        numPaid !== UNTIL_FURTHER_NOTICE_NUM_PYMTS
      ) {
        setEditEndOption('count');
        setEditNumPayments(numPaid);
      } else {
        setEditEndOption('until');
        setEditNumPayments('');
      }
    } catch {
      if (seq === detailSeq.current) {
        const inline = 'Could not load standing order details. Please retry.';
        setDetailError(inline);
        toast.error(inline);
      }
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }

  /**
   * Amend the open standing order — the FULL editable set CCF-101 marks editable
   * (beneficiary name/reference + remit data + frequency/pay-date/amount/
   * number-of-payments), via the dedicated standing-order-amend op (#113).
   */
  async function amendOrder(): Promise<void> {
    if (!detail?.PaymentsSeq) {
      toast.error('This standing order cannot be amended (missing reference).');
      return;
    }
    setAmendError('');
    // Gateway caps BeneficiaryName at 18 (R4 Amend-Standing-Order spec / error
    // code 10006) — guide the agent before submit so the technical gateway
    // message never surfaces (#134).
    const nErr = validateDetailField('Beneficiary name', editName, 18, true);
    const rErr = validateDetailField(
      'Beneficiary reference',
      editRef,
      18,
      true,
    );
    const mErr = validateDetailField('Remit data', editRemit, 40, false, true);
    setNameError(nErr);
    setRefError(rErr);
    setRemitError(mErr);
    // Schedule-field validation (mirrors the CREATE form's rules).
    const se: Record<string, string> = {};
    if (!editFrequency) se.frequency = 'Frequency is required.';
    if (!editPayDate.trim()) se.payDate = 'Regular pay date is required.';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(editPayDate.trim()))
      se.payDate = 'Choose a valid date.';
    // The gateway rejects a past regular pay date with code 899 (R4
    // Amend-Standing-Order spec). The amend form pre-fills the order's existing
    // pay date, which may already be in the past — guide the agent before submit
    // rather than letting the generic "Unable to Process Transaction (899)"
    // surface (#135).
    else if (editPayDate.trim() < isoToday())
      se.payDate =
        'Regular pay date cannot be in the past — choose today or a future date.';
    if (!editAmount.trim()) se.amount = 'Regular amount is required.';
    else if (!REG_AMOUNT_PATTERN.test(normalizeAmount(editAmount)))
      se.amount = 'Enter a valid amount (up to 7 digits, e.g. 876.21).';
    if (!editEndOption) se.endOption = 'Choose a payment duration.';
    else if (editEndOption === 'count') {
      const trimmed = editNumPayments.trim();
      if (!/^\d+$/.test(trimmed))
        se.numPayments = 'Enter the number of payments.';
      else {
        const n = Number(trimmed);
        if (n < 1 || n > 999)
          se.numPayments = 'Enter a number of payments between 1 and 999.';
      }
    }
    setScheduleErrors(se);
    if (nErr || rErr || mErr || Object.keys(se).length > 0) return;

    setSubmitting(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order-amend', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: overviewAccount,
          paymentsSeq: detail.PaymentsSeq,
          ...(detail.PaymentsNo ? { paymentsNo: detail.PaymentsNo } : {}),
          beneficiaryName: editName,
          beneficiaryReference: editRef,
          ...(editRemit ? { remitData: editRemit } : {}),
          paymentFrequency:
            editFrequency as (typeof FREQUENCIES)[number]['value'],
          regularPayDate: isoToDdMmYyyy(editPayDate),
          regularAmount: normalizeAmount(editAmount),
          keepEndOfMonth: editFrequency === 'MONTHLY' && editLastDay,
          numberOfPayments:
            editEndOption === 'count'
              ? editNumPayments.trim()
              : UNTIL_FURTHER_NOTICE_NUM_PYMTS,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Surface the gateway Code + Message INLINE beside the action (#80/82/94)
        // as a recoverable warning — not only a fleeting toast.
        const inline = actionErrorText(
          response.status,
          data as { ResultCode?: string; ResultMessage?: string } | undefined,
          'amend',
          apiError,
        );
        setAmendError(inline);
        toast.error(inline);
        return;
      }
      toast.success('Standing order amended successfully.');
      resetDetail();
      await loadOrders(overviewAccount, includeInactive);
    } catch {
      const inline = 'Could not amend the standing order. Please retry.';
      setAmendError(inline);
      toast.error(inline);
    } finally {
      setSubmitting(false);
    }
  }

  /** Cancel the open standing order (CCF-101). */
  async function cancelOrder(): Promise<void> {
    if (!detail?.PaymentsSeq || !detail.PaymentsNo) {
      toast.error(
        'This standing order cannot be cancelled (missing reference).',
      );
      return;
    }
    setAmendError('');
    setSubmitting(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order-cancel', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: overviewAccount,
          paymentsSeq: detail.PaymentsSeq,
          paymentsNo: detail.PaymentsNo,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Surface the gateway Code + Message INLINE beside the action (#80/82/94).
        const inline = actionErrorText(
          response.status,
          data as { ResultCode?: string; ResultMessage?: string } | undefined,
          'cancel',
          apiError,
        );
        setAmendError(inline);
        toast.error(inline);
        return;
      }
      toast.success('Standing order cancelled successfully.');
      resetDetail();
      await loadOrders(overviewAccount, includeInactive);
    } catch {
      const inline = 'Could not cancel the standing order. Please retry.';
      setAmendError(inline);
      toast.error(inline);
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Start the per-row cancel-standing-order flow (#267 — the trashcan). This
   * does NOT cancel anything on its own: it opens a confirmation dialog so the
   * agent must explicitly confirm (with the customer) before the live
   * standing-order-cancel op is called (see {@link confirmCancelDelete}).
   */
  function requestCancel(row: StandingOrderRow): void {
    // #799 — never open the cancel-confirm dialog for a >1-signatory account
    // (the trashcan is disabled, but guard here too so it can never fire).
    if (
      requiresMultipleSignatories(
        accounts.find((a) => a.AccountNumber === overviewAccount),
      )
    )
      return;
    // Only an editable (New / Active / Retry) standing order can be cancelled —
    // the trashcan is disabled for the rest, but guard here too so a
    // cancelled/lapsed/completed order can never open the confirm dialog and
    // trigger the generic gateway 888 (#277).
    if (!isStandingOrderCancellable(row.Status)) return;
    setDeleteError('');
    setDeleteTarget(row);
  }

  /** Dismiss the cancel-confirmation dialog without cancelling (#267). */
  function dismissCancel(): void {
    setDeleteTarget(null);
    setDeleteError('');
  }

  /**
   * Confirm and perform the per-row cancel (#267). Only reached once the agent
   * has confirmed in the dialog. Cancels via the live standing-order-cancel op
   * and, on a real success, closes the dialog and re-reads the listing. A
   * gateway-side failure surfaces the Code + Message inline in the dialog
   * (recoverable), never a false success (#80/82/94/141).
   */
  async function confirmCancelDelete(): Promise<void> {
    const row = deleteTarget;
    if (!row) return;
    if (!row.PaymentsSeq || !row.PaymentsNo) {
      setDeleteError(
        'This standing order cannot be cancelled (missing reference).',
      );
      return;
    }
    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order-cancel', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: overviewAccount,
          paymentsSeq: row.PaymentsSeq,
          paymentsNo: row.PaymentsNo,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        const inline = actionErrorText(
          response.status,
          data as { ResultCode?: string; ResultMessage?: string } | undefined,
          'cancel',
          apiError,
        );
        setDeleteError(inline);
        toast.error(inline);
        return;
      }
      toast.success('Standing order cancelled successfully.');
      setDeleteTarget(null);
      // If the cancelled order's detail panel is open, close it too.
      if (viewAmendId === row.PaymentId) resetDetail();
      await loadOrders(overviewAccount, includeInactive);
    } catch {
      const inline = 'Could not cancel the standing order. Please retry.';
      setDeleteError(inline);
      toast.error(inline);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!isVisa) {
      const cleaned = iban.replace(/\s+/g, '');
      if (!cleaned) e.iban = 'Beneficiary IBAN is required.';
      else if (!/^IE\d{2}[A-Za-z0-9]{15,30}$/.test(cleaned))
        e.iban = 'Enter a valid IBAN.';
    }
    if (!name.trim()) e.name = 'Beneficiary name is required.';
    else if (!BENEFICIARY_CHARS.test(name))
      e.name = 'Beneficiary name contains invalid characters.';
    if (!reference.trim()) e.reference = 'Reference is required.';
    else if (isVisa) {
      // CCF-73: the VISA standing order's reference IS the 16-digit card number.
      if (!/^\d{16}$/.test(reference.trim()))
        e.reference = 'Enter the 16-digit VISA card number.';
    } else if (reference.length > 16)
      e.reference = 'Reference must be 16 characters or fewer.';
    if (!frequency) e.frequency = 'Frequency is required.';
    if (!payDate.trim()) e.payDate = 'Regular pay date is required.';
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(payDate.trim()))
      e.payDate = 'Choose a valid date.';
    else if (payDate.trim() <= isoToday())
      e.payDate = 'Choose a future date (not today).';
    if (!amount.trim()) e.amount = 'Regular amount is required.';
    else if (!REG_AMOUNT_PATTERN.test(normalizeAmount(amount)))
      e.amount = 'Enter a valid amount (up to 7 digits, e.g. 876.21).';
    if (endOption === 'count') {
      const trimmed = numPayments.trim();
      if (!/^\d+$/.test(trimmed))
        e.numPayments = 'Enter the number of payments.';
      else {
        const n = Number(trimmed);
        if (n < 1 || n > 999)
          e.numPayments = 'Enter a number of payments between 1 and 999.';
      }
    }
    if (!endOption) e.endOption = 'Choose a payment duration.';
    return e;
  }

  async function submit(): Promise<void> {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setError('');
    setCreateError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/standing-order-create', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          visa: isVisa,
          ...(isVisa ? {} : { beneficiaryIban: iban.replace(/\s+/g, '') }),
          beneficiaryName: name,
          beneficiaryReference: reference,
          ...(remit ? { remitData: remit } : {}),
          frequency: frequency as (typeof FREQUENCIES)[number]['value'],
          regularPayDate: isoToDdMmYyyy(payDate),
          regularAmount: normalizeAmount(amount),
          keepEndOfMonth: frequency === 'MONTHLY' && lastDayOfMonth,
          numberOfPayments:
            endOption === 'count'
              ? numPayments.trim()
              : UNTIL_FURTHER_NOTICE_NUM_PYMTS,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Surface the gateway Code + Message INLINE beside the action so the
        // failure is recoverable (the form stays open to retry), per #80/82/94.
        const inline = actionErrorText(
          response.status,
          data as { ResultCode?: string; ResultMessage?: string } | undefined,
          'create',
          apiError,
        );
        setCreateError(inline);
        toast.error(inline);
        return;
      }
      setCompleted(true);
      toast.success('Standing order created successfully.');
    } catch {
      const inline = 'Could not create the standing order. Please retry.';
      setCreateError(inline);
      toast.error(inline);
    } finally {
      setSubmitting(false);
    }
  }

  // Client-side pagination of the loaded standing-orders list (#437 client
  // windowing + #829 server pagination). At most STANDING_ORDERS_PAGE_SIZE (10)
  // rows are rendered per page; the agent moves through pages with
  // Previous/Next. More UI pages may exist beyond the loaded rows when the
  // gateway signals `hasMore`, so the page count is the greater of the loaded-row
  // page count and "one more page available". `page` is clamped defensively so a
  // shrunk list (e.g. after a cancel) can never leave the view out of range.
  const loadedPageCount = Math.max(
    1,
    Math.ceil(orders.length / STANDING_ORDERS_PAGE_SIZE),
  );
  const currentPage = Math.min(page, loadedPageCount - 1 + (hasMore ? 1 : 0));
  const pagedOrders = orders.slice(
    currentPage * STANDING_ORDERS_PAGE_SIZE,
    currentPage * STANDING_ORDERS_PAGE_SIZE + STANDING_ORDERS_PAGE_SIZE,
  );
  // "Next" is enabled while there is another LOADED page OR the gateway signals
  // more rows to fetch (#829). Disabled only on the last loaded page with no more.
  const hasNextPage = currentPage < loadedPageCount - 1 || hasMore;
  // The label shows the loaded-page count; when more remain, a trailing "+"
  // signals further pages are lazily fetchable past the loaded rows.
  const pageCount = loadedPageCount;

  // #480 / #655 — the detail panel is editable only for a New/Active/Retry
  // standing order; Cancelled/Lapsed/Completed (`COMPLD`) are view-only (all
  // fields uneditable, no Amend/Cancel actions, heading reads "View standing
  // order").
  // #625 — a Term Loan (TL) standing order is ALWAYS view-only regardless of
  // Status (its detail op isn't called and no field may be amended/updated).
  const detailEditable =
    isStandingOrderEditable(detail?.Status) &&
    !isTermLoanStandingOrder(detail?.Type);

  // #799 (@dylan219) — Standing Order CANCELLATION is not permitted on an account
  // that requires MORE THAN ONE signatory ("two to sign" mandate); the change has
  // to go through the mandate. The signal is the BFF-normalised per-account
  // `Signatories` count (from the gateway `NumofSignatories`), read via the SHARED
  // increment-1 helper so all six blocked action-areas apply the identical rule.
  // Blocks only on a KNOWN count > 1 — an absent/unknown count never blocks. The
  // cancel controls (the per-row trashcan and the detail-panel "Cancel standing
  // order" button) are suppressed and the verbatim block message is shown inline;
  // the BFF `/payments/standing-order-cancel` route enforces the SAME block
  // server-side (#173), so a reload / direct call cannot bypass it. Amend is a
  // SEPARATE action-area (a later increment) and is deliberately left unaffected.
  const overviewBlocksCancel = requiresMultipleSignatories(
    accounts.find((a) => a.AccountNumber === overviewAccount),
  );

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="so-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Email templates (#705): consolidated into a single top-right dropdown
            selector in the page header, mirroring BillPaymentsScreen (#442).
            Each template is prefilled best-effort from the in-context customer
            name and the selected account number, delivered as a formatted
            downloadable Outlook draft (`.eml`) or plain-text `mailto:` draft. */}
        <EmailTemplateMenu
          templates={STANDING_ORDERS_TEMPLATES}
          context={{
            customerName: customer.name ?? '',
            accountNumber: overviewAccount,
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="so-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Standing orders
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {/* Persistent payment-options selector (issue #116): switch directly
          between the four payment options without returning to the dashboard.
          Placed on the LEFT below the heading to match Bill Payments and the
          sibling payment screens (issue #261). */}
      <div className="mb-6 flex gap-6 flex-wrap items-start">
        <PaymentOptionsNav current="Standing orders" />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Standing-orders option + account selection. */}
      <div className="mb-6 flex gap-6 flex-wrap items-start">
        <div className="w-[400px]">
          <Dropdown
            id="so-option"
            label="Standing orders"
            placeholder="Select"
            value={option}
            onChange={(v) => {
              setOption(v);
              setSelectedAccount('');
              resetForm();
              resetOverview();
            }}
            options={[
              { value: 'overview', label: 'Standing order overview' },
              { value: 'create', label: 'Create standing order' },
            ]}
          />
        </div>

        {option === 'create' && (
          <div className="w-[400px]">
            <Dropdown
              id="so-account"
              label="Select account to create standing order"
              placeholder="Select"
              value={selectedAccount}
              onChange={(v) => {
                setSelectedAccount(v);
                resetForm();
              }}
              options={accounts.filter(isStandingOrderEligible).map((a) => ({
                value: a.AccountNumber,
                // Canonical account-dropdown label (#381): "<name> <last4>",
                // matching Transfer Between Accounts (last-4 only per CCF-80).
                // Only standing-order-eligible account types are offered (#529).
                label: accountOptionLabel(a),
              }))}
            />
          </div>
        )}

        {option === 'overview' && (
          <div className="w-[400px]">
            <Dropdown
              id="so-overview-account"
              label="Select account to view standing orders"
              placeholder="Select"
              value={overviewAccount}
              onChange={onOverviewAccountChange}
              options={accounts.map((a) => ({
                value: a.AccountNumber,
                // Canonical account-dropdown label (#381) — matches Transfer.
                label: accountOptionLabel(a),
              }))}
            />
          </div>
        )}
      </div>

      {/* Standing order overview — listing (CCF-72). */}
      {option === 'overview' && overviewAccount && (
        <div className="mt-2">
          {/* "Show all cancelled, lapsed and completed" toggle (CCF-72). */}
          <div className="mb-6">
            <Toggle
              id="show-all-standing-orders-toggle"
              label="Show all cancelled, lapsed and completed"
              checked={includeInactive}
              onChange={onToggleInactive}
            />
          </div>

          {/* #799 (@dylan219) — the selected account requires MORE THAN ONE
              signatory, so a Standing Order cancellation is not permitted (it has
              to go through the mandate). Surface the verbatim block message inline
              above the listing; the per-row trashcan is disabled and the
              detail-panel Cancel action is suppressed. Amend stays available. */}
          {overviewBlocksCancel && (
            <p
              role="alert"
              className="mb-6 text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0"
            >
              {MULTIPLE_SIGNATORIES_MESSAGE}
            </p>
          )}

          {/* Standing orders table — Status / Retry / Type / Frequency / Name /
              Reference / Amount / Number of payments made (CCF-72). Per the
              supervisor's UX refinement (#267 — mirroring Direct Debits #255):
              the agent CLICKS a row directly to open its Amend/Cancel detail
              (no separate "Amend/Cancel" action button), and a trailing
              trashcan column starts the cancel-standing-order flow (which only
              proceeds after the agent confirms). */}
          <div className="overflow-x-auto max-w-[1400px]">
            <table className="w-full border-collapse bg-white" role="table">
              <thead>
                <tr className="bg-[#f3f4f6]">
                  {[
                    'Status',
                    'Retry',
                    'Type',
                    'Frequency',
                    'Name',
                    'Reference',
                    'Amount',
                    'Number of payments made',
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                    >
                      {h}
                    </th>
                  ))}
                  {/* Trailing action (trashcan) column — no visible header. */}
                  <th
                    className="px-4 py-3 border-b border-[#d1d5db]"
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {ordersLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : orders.length > 0 ? (
                  pagedOrders.map((o) => {
                    const soName = o.Name ?? 'standing order';
                    // The listing is per selected debit account, and the amount
                    // is in THAT account's currency — resolve its symbol from the
                    // account object (defaults to EUR when absent). #436.
                    const accountCurrency =
                      accounts.find((a) => a.AccountNumber === overviewAccount)
                        ?.Currency ?? 'EUR';
                    const isSelected = viewAmendId === o.PaymentId;
                    // A full-cell clickable button per data cell so the whole
                    // row selects on click and stays keyboard-accessible (#267,
                    // mirroring Direct Debits #255). Only the primary Name cell
                    // carries the "Amend/Cancel <name>" accessible label and is
                    // keyboard-tabbable, so there is exactly one focusable
                    // labelled control per row (no strict-mode collision). The
                    // remaining cells are NOT keyboard-tabbable (tabIndex=-1) but
                    // stay in the accessibility tree so each cell keeps its own
                    // text as its accessible name — the Status cell reads
                    // "Cancelled"/"Lapsed" and the row name still carries the
                    // standing-order name (CCF-72 columns + #267 row-select).
                    const cellBtn = (
                      value: React.ReactNode,
                      labelled: boolean,
                    ): React.JSX.Element => (
                      <button
                        type="button"
                        onClick={() => void openDetail(o)}
                        {...(labelled
                          ? { 'aria-label': `Amend/Cancel ${soName}` }
                          : { tabIndex: -1 })}
                        className="w-full h-full px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] bg-transparent border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#005fcc]"
                      >
                        {value}
                      </button>
                    );
                    return (
                      <tr
                        key={o.PaymentId}
                        aria-selected={isSelected}
                        className={`border-b border-[#e5e7eb] ${
                          isSelected ? 'bg-[#f3f4f6]' : 'hover:bg-[#f9fafb]'
                        }`}
                      >
                        <td className="p-0">
                          {cellBtn(soStatusLabel(o.Status), false)}
                        </td>
                        <td className="p-0">
                          {cellBtn(retryDisplay(o.Retry), false)}
                        </td>
                        <td className="p-0">
                          {cellBtn(soTypeLabel(o.Type), false)}
                        </td>
                        <td className="p-0">{cellBtn(o.Frequency, false)}</td>
                        <td className="p-0">{cellBtn(o.Name, true)}</td>
                        <td className="p-0">{cellBtn(o.Reference, false)}</td>
                        <td className="p-0">
                          {cellBtn(
                            formatAmount(o.Amount, accountCurrency),
                            false,
                          )}
                        </td>
                        <td className="p-0">
                          {cellBtn(o.NumberOfPaymentsMade, false)}
                        </td>
                        {/* Delete (trashcan) — starts the cancel-standing-order
                            flow. Unlike Direct Debits (whose delete endpoint is
                            not yet contracted), the standing-order-cancel op IS
                            live, so the trashcan opens a confirmation step and
                            only cancels once the agent confirms (#267). It is
                            DISABLED for a standing order that is already
                            cancelled / lapsed / completed: only an editable
                            (New / Active / Retry) order can be cancelled (#655),
                            and clicking a view-only one otherwise made the real
                            gateway reject with a generic "Unable to Process
                            Transaction (888)" (#277). */}
                        <td className="px-4 py-3 text-right w-px">
                          <button
                            type="button"
                            aria-label={`Delete ${soName}`}
                            onClick={() => requestCancel(o)}
                            disabled={
                              overviewBlocksCancel ||
                              !isStandingOrderCancellable(o.Status)
                            }
                            title={
                              overviewBlocksCancel
                                ? MULTIPLE_SIGNATORIES_MESSAGE
                                : isStandingOrderCancellable(o.Status)
                                  ? undefined
                                  : `This standing order is already ${(
                                      soStatusLabel(o.Status) || 'inactive'
                                    ).toLowerCase()} and cannot be cancelled.`
                            }
                            className="p-2 rounded text-[#1D252C] hover:bg-[#f3f4f6] focus:outline-none focus:ring-2 focus:ring-[#005fcc] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            <svg
                              className="size-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  showOrders && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                      >
                        No standing orders available.
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Next/Previous pagination (CCF-72 / #437). The list shows a maximum
              of STANDING_ORDERS_PAGE_SIZE (10) rows per page; the agent pages
              through the loaded orders with these controls. Previous is disabled
              on the first page, Next on the last (a single page of ≤10 disables
              both). @dylan219 #437: "make each page a maximum of 10 and allow
              user click next to see the next 10". */}
          {showOrders && orders.length > 0 && (
            <div className="mt-6 flex items-center gap-4">
              <button
                type="button"
                disabled={currentPage <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Previous page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                &lt; Previous
              </button>
              <span
                aria-live="polite"
                className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
              >
                {/* #829: a trailing "+" when the gateway signals further pages
                    that are fetched lazily past the currently loaded rows. */}
                Page {currentPage + 1} of {pageCount}
                {hasMore ? '+' : ''}
              </span>
              <button
                type="button"
                disabled={!hasNextPage || loadingMore}
                onClick={() => void goToNextPage()}
                aria-label="Next page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                {loadingMore ? 'Loading…' : 'Next >'}
              </button>
            </div>
          )}

          {/* View/Amend/Cancel detail panel — SCRN-36 / CCF-101. Read-only
              Beneficiary IBAN/BIC; editable beneficiary name/reference + remit
              data + the schedule. Type, Number of payments made and Status were
              removed from the view at the agents' request (issue #242). */}
          {viewAmendId !== null && (
            <div className="mt-8 max-w-[900px] border-2 border-[#dad7d3] bg-white p-8">
              <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8">
                {detailEditable
                  ? 'Amend/Cancel standing order'
                  : 'View standing order'}
              </h2>

              {detailLoading ? (
                <p
                  role="status"
                  className="text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
                >
                  Loading details…
                </p>
              ) : detail ? (
                <>
                  {/* Read-only identity (CCF-101): Beneficiary IBAN/BIC.
                      Type, Number of payments made and Status were removed from
                      the individual standing-order view at the servicing agents'
                      request (issue #242). Frequency / regular amount /
                      number-of-payments are EDITABLE (#113), rendered below. */}
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                    {[
                      ['Beneficiary IBAN', detail.BeneficiaryIban],
                      ['Beneficiary BIC', detail.BeneficiaryBic],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <label className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
                          {label}
                        </label>
                        <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                          {value ? value : '—'}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Editable beneficiary name + reference (CCF-101). */}
                  <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                      <label
                        htmlFor="amend-so-beneficiary-name"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        Beneficiary name
                      </label>
                      <input
                        type="text"
                        id="amend-so-beneficiary-name"
                        value={editName}
                        onChange={(e) => {
                          setEditName(e.target.value);
                          setNameError('');
                        }}
                        maxLength={18}
                        disabled={!detailEditable}
                        aria-invalid={!!nameError}
                        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                          detailEditable
                            ? 'bg-white'
                            : 'bg-[#f3f4f6] text-[#6b7280]'
                        } ${
                          nameError ? 'border-[#dc2626]' : 'border-[#979c9f]'
                        }`}
                      />
                      <div className="min-h-[20px]">
                        {nameError && (
                          <p
                            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                            role="alert"
                          >
                            {nameError}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="amend-so-beneficiary-reference"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        Beneficiary reference, max 18 characters
                      </label>
                      <input
                        type="text"
                        id="amend-so-beneficiary-reference"
                        value={editRef}
                        onChange={(e) => {
                          setEditRef(e.target.value);
                          setRefError('');
                        }}
                        maxLength={18}
                        disabled={!detailEditable}
                        aria-invalid={!!refError}
                        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                          detailEditable
                            ? 'bg-white'
                            : 'bg-[#f3f4f6] text-[#6b7280]'
                        } ${
                          refError ? 'border-[#dc2626]' : 'border-[#979c9f]'
                        }`}
                      />
                      <div className="min-h-[20px]">
                        {refError && (
                          <p
                            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                            role="alert"
                          >
                            {refError}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Editable remit data (CCF-101, optional). */}
                  <div className="mt-6 grid grid-cols-2 gap-x-8">
                    <div>
                      <label
                        htmlFor="amend-so-remit-data"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        Remit data
                      </label>
                      <textarea
                        id="amend-so-remit-data"
                        value={editRemit}
                        onChange={(e) => {
                          setEditRemit(e.target.value);
                          setRemitError('');
                        }}
                        maxLength={40}
                        rows={5}
                        disabled={!detailEditable}
                        aria-invalid={!!remitError}
                        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] resize-none ${
                          detailEditable
                            ? 'bg-white'
                            : 'bg-[#f3f4f6] text-[#6b7280]'
                        } ${
                          remitError ? 'border-[#dc2626]' : 'border-[#979c9f]'
                        }`}
                      />
                      <div className="min-h-[20px]">
                        {remitError && (
                          <p
                            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                            role="alert"
                          >
                            {remitError}
                          </p>
                        )}
                      </div>
                    </div>
                    <div></div>
                  </div>

                  {/* Editable schedule fields (CCF-101 / #113): frequency,
                      regular pay date, regular amount, number of payments. */}
                  {/* Two explicit column CELLS (not row-flow auto-placement).
                      The right column is a single flex-column so "Regular pay
                      date" → "Last day of month" → "Regular amount" stack
                      SNUGLY, one directly beneath the next, regardless of the
                      left column's height. The earlier row-flow grid + a
                      `col-start-2` checkbox let the tall "Number of payments"
                      fieldset in the left column push the checkbox down into its
                      own row, leaving it floating mid-column (#613). Grouping the
                      right-hand fields in their own cell keeps the checkbox
                      directly under "Regular pay date" (the #531/!799 intent) —
                      the checkbox stays in column 2 and DOM order == visual
                      order, so tab order is unaffected. */}
                  <div className="mt-8 grid grid-cols-2 gap-x-8 gap-y-2 items-start">
                    {/* LEFT column: Frequency, then Number of payments. */}
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-2">
                        <Dropdown
                          id="amend-so-frequency"
                          label="Frequency"
                          placeholder="Select frequency"
                          value={editFrequency}
                          disabled={!detailEditable}
                          onChange={(v) => {
                            setEditFrequency(v);
                            if (v !== 'MONTHLY') setEditLastDay(false);
                            setScheduleErrors((e) => ({ ...e, frequency: '' }));
                          }}
                          options={FREQUENCIES.map((f) => ({
                            value: f.value,
                            label: f.label,
                          }))}
                        />
                        <div className="min-h-[18px]">
                          {scheduleErrors.frequency && (
                            <p
                              className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              role="alert"
                            >
                              {scheduleErrors.frequency}
                            </p>
                          )}
                        </div>
                      </div>
                      <fieldset className="flex flex-col gap-2">
                        <legend className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-1">
                          Number of payments
                        </legend>
                        <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                          <input
                            type="radio"
                            name="amend-so-end-option"
                            checked={editEndOption === 'until'}
                            disabled={!detailEditable}
                            onChange={() => {
                              setEditEndOption('until');
                              setScheduleErrors((e) => ({
                                ...e,
                                endOption: '',
                                numPayments: '',
                              }));
                            }}
                            className="size-[18px] accent-[#FF703E]"
                          />
                          Until further notice
                        </label>
                        <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                          <input
                            type="radio"
                            name="amend-so-end-option"
                            checked={editEndOption === 'count'}
                            disabled={!detailEditable}
                            onChange={() => {
                              setEditEndOption('count');
                              setScheduleErrors((e) => ({
                                ...e,
                                endOption: '',
                              }));
                            }}
                            className="size-[18px] accent-[#FF703E]"
                          />
                          Specified number
                        </label>
                        {editEndOption === 'count' && (
                          <Field
                            id="amend-so-num-payments"
                            label="No. of payments"
                            value={editNumPayments}
                            disabled={!detailEditable}
                            onChange={(v) => {
                              setEditNumPayments(v);
                              setScheduleErrors((e) => ({
                                ...e,
                                numPayments: '',
                              }));
                            }}
                            placeholder="Enter number"
                            error={scheduleErrors.numPayments}
                          />
                        )}
                        <div className="min-h-[18px]">
                          {scheduleErrors.endOption && (
                            <p
                              className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              role="alert"
                            >
                              {scheduleErrors.endOption}
                            </p>
                          )}
                        </div>
                      </fieldset>
                    </div>
                    {/* RIGHT column: Regular pay date, then the "Last day of
                        month" checkbox directly beneath it, then Regular amount
                        — all in one flex-column CELL so they stack snugly
                        (#613 fixes the mid-column float; #531/!799 intent kept:
                        the checkbox lives under "Regular pay date" in column 2). */}
                    <div
                      className="flex flex-col gap-2"
                      data-testid="amend-so-right-column"
                    >
                      <Field
                        id="amend-so-pay-date"
                        label="Regular pay date"
                        type="date"
                        value={editPayDate}
                        disabled={!detailEditable}
                        onChange={(v) => {
                          setEditPayDate(v);
                          setScheduleErrors((e) => ({ ...e, payDate: '' }));
                        }}
                        hint={
                          editFrequency === 'MONTHLY' && editLastDay
                            ? 'Auto-set to the last day of the month when ticked — adjust if needed.'
                            : undefined
                        }
                        error={scheduleErrors.payDate}
                      />
                      {/* Last-day-of-month (monthly only) — CCF-101. Sits
                          directly under "Regular pay date" (#531/#613). */}
                      {editFrequency === 'MONTHLY' && (
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="amend-so-last-day"
                            checked={editLastDay}
                            disabled={!detailEditable}
                            onChange={() => {
                              setEditLastDay((c) => {
                                const next = !c;
                                // Mirrors the CREATE form (#288): KeepEoM='Y'
                                // does NOT waive RegPayDate on the gateway, so
                                // auto-fill the last-day-of-month date instead.
                                if (next) {
                                  setEditPayDate(isoLastDayOfMonth());
                                  setScheduleErrors((e) => ({
                                    ...e,
                                    payDate: '',
                                  }));
                                }
                                return next;
                              });
                            }}
                            className="size-[20px] accent-[#FF703E] cursor-pointer"
                          />
                          <label
                            htmlFor="amend-so-last-day"
                            className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                          >
                            Last day of month
                          </label>
                        </div>
                      )}
                      <Field
                        id="amend-so-amount"
                        label="Regular amount"
                        value={editAmount}
                        disabled={!detailEditable}
                        onChange={(v) => {
                          setEditAmount(sanitizeAmountInput(v));
                          setScheduleErrors((e) => ({ ...e, amount: '' }));
                        }}
                        onBlur={() => setEditAmount((a) => normalizeAmount(a))}
                        placeholder="Enter amount"
                        inputMode="decimal"
                        error={scheduleErrors.amount}
                      />
                    </div>
                  </div>

                  {/* Confirm-all-details gate → Amend / Cancel (CCF-101). Only a
                      view-EDITABLE standing order (Active / Retry) offers the
                      amend/cancel actions; a view-only order (Cancelled / Lapsed
                      / Completed) shows no confirm gate or action buttons (#480). */}
                  {detailEditable && (
                    <>
                      <div className="mt-6 flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="amend-so-confirm-details"
                          checked={confirmDetails}
                          onChange={() => setConfirmDetails((c) => !c)}
                          className="size-[20px] accent-[#FF703E] cursor-pointer"
                        />
                        <label
                          htmlFor="amend-so-confirm-details"
                          className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                        >
                          Confirm all details
                        </label>
                      </div>

                      {confirmDetails && (
                        <>
                          {/* Inline, recoverable amend/cancel failure — the
                              gateway Code + Message beside the action (#80/82/94). */}
                          {amendError && (
                            <p
                              role="alert"
                              className="mt-6 text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                            >
                              {amendError}
                            </p>
                          )}
                          <div className="mt-6 flex gap-4">
                            <button
                              type="button"
                              disabled={submitting}
                              onClick={() => void amendOrder()}
                              className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {submitting ? 'Working…' : 'Amend standing order'}
                            </button>
                            {/* #799 — the Cancel action is SUPPRESSED on a
                                >1-signatory account (it must go through the
                                mandate); the verbatim block message is shown
                                inline in its place. Amend stays available (a
                                separate action-area / later increment). */}
                            {overviewBlocksCancel ? (
                              <p
                                role="alert"
                                className="self-center text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                              >
                                {MULTIPLE_SIGNATORIES_MESSAGE}
                              </p>
                            ) : (
                              <button
                                type="button"
                                disabled={submitting}
                                onClick={() => void cancelOrder()}
                                className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Cancel standing order
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                /* Inline, recoverable failure — surfaces the gateway Code +
                   Message beside the action so the agent sees WHY the detail
                   did not load and can retry (#103). */
                <div className="flex flex-col gap-4">
                  <p
                    role="alert"
                    className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                  >
                    {detailError ||
                      'Could not load standing order details. Please retry.'}
                  </p>
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        const o = orders.find(
                          (r) => r.PaymentId === viewAmendId,
                        );
                        if (o) void openDetail(o);
                      }}
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cancel-standing-order confirmation dialog (#267). Opened by the
              per-row trashcan; the live standing-order-cancel op is only called
              once the agent explicitly confirms. */}
          {deleteTarget && (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-so-dialog-title"
            >
              <div className="w-full max-w-[520px] rounded-lg border-2 border-[#dad7d3] bg-white p-8 shadow-xl">
                <h2
                  id="cancel-so-dialog-title"
                  className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4"
                >
                  Cancel standing order?
                </h2>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
                  This will cancel the standing order
                  {deleteTarget.Name ? ` to ${deleteTarget.Name}` : ''}
                  {deleteTarget.Reference ? ` (${deleteTarget.Reference})` : ''}
                  . This cannot be undone. Confirm the cancellation with the
                  customer before proceeding.
                </p>
                {deleteError && (
                  <p
                    role="alert"
                    className="mb-4 p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    {deleteError}
                  </p>
                )}
                <div className="flex gap-4">
                  <button
                    type="button"
                    disabled={deleteSubmitting}
                    onClick={() => void confirmCancelDelete()}
                    className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteSubmitting ? 'Cancelling…' : 'Confirm cancellation'}
                  </button>
                  <button
                    type="button"
                    disabled={deleteSubmitting}
                    onClick={dismissCancel}
                    className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Keep standing order
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create standing order (CCF-14 / CCF-73). */}
      {option === 'create' && selectedAccount && (
        <div className="max-w-[900px] border-2 border-[#dad7d3] bg-white p-8">
          {(() => {
            // #463 / #414: show the account OWNER name(s) — ALL owners, joint
            // included — above the Create buttons, instead of the raw account
            // number. Sourced from the same `customer/accounts/list` `Owners`
            // field the Dashboard tile and Historic Transactions read. Falls
            // back to the account number only when owner data is absent.
            const acct = accounts.find(
              (a) => a.AccountNumber === selectedAccount,
            );
            const owners = formatOwners(acct?.Owners);
            return owners ? (
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
                <span className="font-bold">Account owners:</span> {owners}
              </p>
            ) : (
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
                <span className="font-bold">Account:</span> {selectedAccount}
              </p>
            );
          })()}

          <div className="flex gap-4 mb-8">
            <button
              type="button"
              onClick={() => openForm(false)}
              aria-label="Create standing order"
              className={`px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
                showForm && !isVisa
                  ? 'bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E]'
                  : 'bg-white text-[#FF703E] border-2 border-[#FF703E] hover:bg-[#fff5f2]'
              }`}
            >
              Create standing order
            </button>
            <button
              type="button"
              onClick={() => openForm(true)}
              aria-label="Create standing order to Visa"
              className={`px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
                showForm && isVisa
                  ? 'bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E]'
                  : 'bg-white text-[#FF703E] border-2 border-[#FF703E] hover:bg-[#fff5f2]'
              }`}
            >
              Create standing order to Visa
            </button>
          </div>

          {showForm && !completed && (
            <div>
              <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8">
                {isVisa
                  ? 'Create standing order to Visa'
                  : 'Create standing order'}
              </h2>

              <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-2">
                <Field
                  id="so-iban"
                  label="Beneficiary IBAN"
                  value={isVisa ? 'IE55IPBS99073270000052' : iban}
                  onChange={setIban}
                  placeholder="IE55IPBS99073270000052"
                  readOnlyText={isVisa}
                  error={errors.iban}
                />
                <Field
                  id="so-name"
                  label="Beneficiary name"
                  value={name}
                  onChange={setName}
                  placeholder="Enter beneficiary name"
                  error={errors.name}
                />
                <Field
                  id="so-reference"
                  label={
                    isVisa
                      ? 'Beneficiary reference (16-digit VISA card number)'
                      : 'Beneficiary reference (max 16 characters)'
                  }
                  value={reference}
                  onChange={setReference}
                  placeholder="Enter reference"
                  maxLength={16}
                  error={errors.reference}
                />
                <div className="flex flex-col gap-2">
                  <Dropdown
                    id="so-frequency"
                    label="Frequency"
                    placeholder="Select frequency"
                    value={frequency}
                    onChange={(v) => {
                      setFrequency(v);
                      if (v !== 'MONTHLY') setLastDayOfMonth(false);
                    }}
                    options={FREQUENCIES.filter(
                      (f) => !isVisa || VISA_FREQUENCY_VALUES.has(f.value),
                    ).map((f) => ({
                      value: f.value,
                      label: f.label,
                    }))}
                  />
                  <div className="min-h-[18px]">
                    {errors.frequency && (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                        role="alert"
                      >
                        {errors.frequency}
                      </p>
                    )}
                  </div>
                </div>
                {/* LEFT column CELL: "Regular pay date" and — when Monthly —
                    the "Last day of month" checkbox directly beneath it, grouped
                    in a single flex-column so the checkbox renders SNUGLY under
                    the pay-date field (#661). Row-flow auto-placement previously
                    flowed the checkbox into row 3 / column 2 (beside the pay
                    date) and pushed "Regular amount" down into column 1, so the
                    checkbox floated to the RIGHT of the field instead of under
                    it. Keeping the pair in their own cell leaves "Regular amount"
                    as the column-2 cell next to "Regular pay date" — matches the
                    #661 expected screenshot, mirroring the Amend form (#531/#613)
                    while preserving the Create form's pay-date-left/amount-right
                    layout. */}
                <div
                  className="flex flex-col gap-2"
                  data-testid="create-so-pay-date-cell"
                >
                  <Field
                    id="so-pay-date"
                    label="Regular pay date"
                    type="date"
                    min={isoTomorrow()}
                    value={payDate}
                    onChange={setPayDate}
                    hint={
                      frequency === 'MONTHLY' && lastDayOfMonth
                        ? 'Automatically set to the last day of the month.'
                        : 'Pick a future date (not today).'
                    }
                    error={errors.payDate}
                    disabled={frequency === 'MONTHLY' && lastDayOfMonth}
                  />
                  {/* Last-day-of-month (monthly only). */}
                  {frequency === 'MONTHLY' && (
                    <div className="mb-2 flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="so-last-day"
                        checked={lastDayOfMonth}
                        onChange={() => {
                          setLastDayOfMonth((c) => {
                            const next = !c;
                            // The gateway always requires RegPayDate, even with
                            // KeepEoM='Y' — auto-fill it with the actual last day
                            // of the month so the agent doesn't have to (#288),
                            // rather than incorrectly waiving the requirement.
                            if (next) {
                              setPayDate(isoLastDayOfMonth());
                              setErrors((e) => ({ ...e, payDate: '' }));
                            }
                            return next;
                          });
                        }}
                        className="size-[20px] accent-[#FF703E] cursor-pointer"
                      />
                      <label
                        htmlFor="so-last-day"
                        className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                      >
                        Last day of month
                      </label>
                    </div>
                  )}
                </div>
                <Field
                  id="so-amount"
                  label="Regular amount"
                  value={amount}
                  onChange={(v) => setAmount(sanitizeAmountInput(v))}
                  onBlur={() => setAmount((a) => normalizeAmount(a))}
                  placeholder="Enter amount"
                  inputMode="decimal"
                  hint="Digits and a decimal point only (e.g. 876.21)."
                  error={errors.amount}
                />
              </div>

              <div className="grid grid-cols-2 gap-x-8 mb-6">
                <div className="flex flex-col gap-2">
                  <label
                    htmlFor="so-remit"
                    className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                  >
                    Remit data
                  </label>
                  <textarea
                    id="so-remit"
                    value={remit}
                    onChange={(e) => setRemit(e.target.value)}
                    placeholder="Enter remit data"
                    rows={5}
                    className="w-full px-4 py-2 border border-[#d1d5db] rounded-md bg-white font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 resize-none"
                  />
                </div>

                <fieldset className="flex flex-col gap-2">
                  <legend className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-1">
                    Number of payments
                  </legend>
                  <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                    <input
                      type="radio"
                      name="so-end-option"
                      checked={endOption === 'until'}
                      onChange={() => setEndOption('until')}
                      className="size-[18px] accent-[#FF703E]"
                    />
                    Until further notice
                  </label>
                  <label className="flex items-center gap-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer">
                    <input
                      type="radio"
                      name="so-end-option"
                      checked={endOption === 'count'}
                      onChange={() => setEndOption('count')}
                      className="size-[18px] accent-[#FF703E]"
                    />
                    Specified number
                  </label>
                  {endOption === 'count' && (
                    <Field
                      id="so-num-payments"
                      label="No. of payments"
                      value={numPayments}
                      onChange={setNumPayments}
                      placeholder="Enter number"
                      error={errors.numPayments}
                    />
                  )}
                  <div className="min-h-[18px]">
                    {errors.endOption && (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                        role="alert"
                      >
                        {errors.endOption}
                      </p>
                    )}
                  </div>
                </fieldset>
              </div>

              <div className="flex items-center gap-3 mb-6">
                <input
                  type="checkbox"
                  id="so-confirm"
                  checked={confirmed}
                  onChange={() => setConfirmed((c) => !c)}
                  className="size-[20px] accent-[#FF703E] cursor-pointer"
                />
                <label
                  htmlFor="so-confirm"
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                >
                  Confirmed all payment details above with customer prior to
                  proceeding
                </label>
              </div>

              {confirmed && (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void submit()}
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-md font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Working…' : 'Complete'}
                </button>
              )}
              {/* Inline, recoverable create failure — gateway Code + Message
                  beside the action so the agent can retry (#80/82/94; #141). */}
              {createError && (
                <p
                  role="alert"
                  className="mt-4 mb-0 p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                >
                  {createError}
                </p>
              )}
            </div>
          )}

          {completed && (
            <div className="py-4" role="alert" aria-live="polite">
              <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#16a34a] m-0">
                Standing order created successfully
              </p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
